import React, { Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Loader } from "../Components/Loader";
import useUser from "../Core/hook/useUser";
import { Privileges } from "../Core/Enum/enum";
import AccessDenied from "../Components/AccessDenied/AccessDenied";
import NotFound from "../Components/NotFound/NotFound";

/* ---------------------------------------------------------------------- *
 * Route components — code split
 *
 * These 102 page components used to be static `import X from "../Pages/X"`
 * declarations. Because every one of them was reachable from this module,
 * webpack had to put all of them — Payroll, the PDF and print views, jspdf,
 * xlsx, Highcharts, FullCalendar, react-quill, the dead POS screens — into a
 * single `main.js`. It measured 5,795 KB raw / 1,373 KB gzipped, and a user
 * logging in to look at the dashboard downloaded, parsed and executed every
 * byte of it first, including the payroll PDF generator they will never open.
 *
 * `React.lazy` gives each page its own chunk, fetched when its route is first
 * visited. This needed no new Suspense boundaries: every `<Route>` below was
 * already wrapped in `<Suspense fallback={<Loader />}>`, so the fallback path
 * existed and was simply never exercised. Nothing about which component
 * renders on which path, or about the privilege checks around them, changes.
 *
 * The Suspense fallback sits *inside* `<MiniDrawer>`, so the sidebar and top
 * bar stay mounted across a chunk fetch — the shell never blinks.
 * ---------------------------------------------------------------------- */
const Dashboard = React.lazy(() => import("../Pages/Dashboard"));

const Setting = React.lazy(() => import("../Pages/Setting/index"));
const ClientReportSetting = React.lazy(() => import("../Pages/Setting/clientReportSetting"));

const CreateDepartment = React.lazy(() => import("../Pages/Department/Create"));
const ListDepartment = React.lazy(() => import("../Pages/Department/List"));

const CreateUser = React.lazy(() => import("../Pages/User/create"));
const ListUser = React.lazy(() => import("../Pages/User/List"));

const CreateUserRole = React.lazy(() => import("../Pages/UserRole/Create"));
const ListUserRole = React.lazy(() => import("../Pages/UserRole/List"));

const CreateUserPrivilege = React.lazy(() => import("../Pages/Privilege/Create"));
const ListUserPrivilege = React.lazy(() => import("../Pages/Privilege/List"));

const CreateRestaurant = React.lazy(() => import("../Pages/Restaurant/Create"));
const ListRestaurant = React.lazy(() => import("../Pages/Restaurant/List"));
const EditRestaurant = React.lazy(() => import("../Pages/Restaurant/Edit"));

const ThemeColor = React.lazy(() => import("../Pages/ThemeColor/Create"));

const CreatePrinter = React.lazy(() => import("../Pages/Printer/Create/index"));
const ListPrinter = React.lazy(() => import("../Pages/Printer/List"));

const CreateDriver = React.lazy(() => import("../Pages/Staff/Create"));
const ListDriver = React.lazy(() => import("../Pages/Staff/List"));
const DriverHistory = React.lazy(() => import("../Pages/Staff/View"));
const EmployeePdf = React.lazy(() => import("../Pages/Staff/Print/employeePdf"));
const AllEmployeepdf = React.lazy(() => import("../Pages/Staff/Print/allEmployeepdf"));
const EmployeePayrollpdf = React.lazy(() => import("../Pages/Staff/staffPdf/index"));

const ListPuchDetails = React.lazy(() => import("../Pages/Punch/List"));

const ListSchedule = React.lazy(() => import("../Pages/Attendance/List"));
const AdminCreateSchedule = React.lazy(() => import("../Pages/Attendance/AdminEdit/AdminCreate"));

const CreatePosition = React.lazy(() => import("../Pages/Position/Create"));
const ListPosition = React.lazy(() => import("../Pages/Position/List"));

const ListPayroll = React.lazy(() => import("../Pages/Payroll/List"));
const ConfirmPayroll = React.lazy(() => import("../Pages/Payroll/ConfirmPayroll/index"));
const ViewPayroll = React.lazy(() => import("../Pages/Payroll/View"));
const EditPayroll = React.lazy(() => import("../Pages/Payroll/Edit"));
const EmpReportPayroll = React.lazy(() => import("../Pages/Payroll/FullView"));

const ReScheduleList = React.lazy(() => import("../Pages/ScheduleList/List"));
const CreateScheduleList = React.lazy(() => import("../Pages/ScheduleList/Create"));

const EmployeeTimeScheduleList = React.lazy(() => import("../Pages/EmployeeTimeSchedule/List"));
const EmployeeWeeklyReport = React.lazy(() => import("../Pages/EmployeeTimeSchedule/WeeklyReport"));

const CreateCustomer = React.lazy(() => import("../Pages/Customer/Create"));
const ListCustomer = React.lazy(() => import("../Pages/Customer/List"));
const CustomerTransaction = React.lazy(() => import("../Pages/CustomerTransaction"));
const CustomerDeals = React.lazy(() => import("../Pages/Customer/Deals"));

const CustomerMeeting = React.lazy(() => import("../Pages/Meeting/List/index"));
const CreateMeeting = React.lazy(() => import("../Pages/Meeting/Create/createMeetingAll"));
const SettingMeeting = React.lazy(() => import("../Pages/Meeting/Setting/index"));
const ProfileMetting = React.lazy(() => import("../Pages/Meeting/List/profile"));
const CreateMeetingClient = React.lazy(() => import("../Pages/Meeting/Create/createMeetingClient"));
const CreateMeetingStaff = React.lazy(() => import("../Pages/Meeting/Create/createMeetingStaff"));

const Looping = React.lazy(() => import("../Pages/Looping"));

//-----------------------------------------------

const CreateProduct = React.lazy(() => import("../Pages/Product/Create"));
const ListProduct = React.lazy(() => import("../Pages/Product/List"));
const ProductDetails = React.lazy(() => import("../Pages/Product/View/index"));

const CreateCategory = React.lazy(() => import("../Pages/Category/Create"));
const ListCategory = React.lazy(() => import("../Pages/Category/List"));

const CreateLooping = React.lazy(() => import("../Pages/Looping/Create"));
const ListLooping = React.lazy(() => import("../Pages/Looping/List"));

const CreateProductBrand = React.lazy(() => import("../Pages/ProductBrand/Create"));
const ListProductBrand = React.lazy(() => import("../Pages/ProductBrand/List"));

const CustomerProfile = React.lazy(() => import("../Pages/Profile/index"));
const CoverageProfile = React.lazy(() => import("../Pages/Profile/View"));

const ListPolicyScenarios = React.lazy(() => import("../Pages/PolicyScenarios/List up/index"));

const BasicDealEdit = React.lazy(() => import("../Pages/Deal/Edit/BasicDealEdit"));

const EditCoverageDeal = React.lazy(() => import("../Pages/Deal/EditCoverage"));
const CreateCoverageDeal = React.lazy(() => import("../Pages/Deal/CreateCoverage/index"));

const SummaryReport = React.lazy(() => import("../Pages/SummaryReport/index"));
const GeneratePdf = React.lazy(() => import("../Pages/SummaryReport/Pdf/secondpdf"));
const SecondpdfGenerate = React.lazy(() => import("../Pages/SummaryReport/Pdf/index"));
const CoveragePdf = React.lazy(() => import("../Pages/SummaryReport/CoveragePdf/secoundpdf"));
const CoverageSecoundPdf = React.lazy(() => import("../Pages/SummaryReport/CoveragePdf/index"));

const CreateTodo = React.lazy(() => import("../Pages/ToDo/Create"));
const ListToDo = React.lazy(() => import("../Pages/ToDo/List"));
const ConversationsMessage = React.lazy(() => import("../Pages/ToDo/Edit/index"));

const PolicyListList = React.lazy(() => import("../Pages/PolicyList/List"));

const EmailSetting = React.lazy(() => import("../Pages/EmailSetting/List"));
const EmailSettingCreate = React.lazy(() => import("../Pages/EmailSetting/Create"));
const EmailTemplateCreate = React.lazy(() => import("../Pages/EmailSetting/EmailTemplate/index"));
const SmsTemplateCreate = React.lazy(() => import("../Pages/EmailSetting/EmailTemplate/smsTemplet"));


const CreateNewsletters = React.lazy(() => import("../Pages/Newsletters/Create"));
const ListNewsletters = React.lazy(() => import("../Pages/Newsletters/List"));
const TypeNewsletters = React.lazy(() => import("../Pages/Newsletters/Type"));
const ViewNewsletters = React.lazy(() => import("../Pages/Newsletters/Type/viewEmail"));
const ListClientEmail = React.lazy(() => import("../Pages/Newsletters/Type/listEmailVeiw"));
const ViewNewslettersSms = React.lazy(() => import("../Pages/Newsletters/Type/viewSms"));
const ListClientEmailSms = React.lazy(() => import("../Pages/Newsletters/Type/listSmsVeiw"));
const ClientEmailCreate = React.lazy(() => import("../Pages/Newsletters/Type/emailCreate"));
const ClientSmsCreate = React.lazy(() => import("../Pages/Newsletters/Type/smsCreate"));

const CreateClientType = React.lazy(() => import("../Pages/ClientType/Create"));
const ListClientType = React.lazy(() => import("../Pages/ClientType/List"));

const CustomerTask = React.lazy(() => import("../Pages/Task/List/index"));
const CreateTask = React.lazy(() => import("../Pages/Task/Create/createMeetingAll"));
const SettingTask = React.lazy(() => import("../Pages/Task/Setting/index"));
const ProfileTask = React.lazy(() => import("../Pages/Task/List/profile"));
const CreateTaskClient = React.lazy(() => import("../Pages/Task/Create/createMeetingClient"));
const CreateTaskStaff = React.lazy(() => import("../Pages/Task/Create/createMeetingStaff"));

const WorkshopList = React.lazy(() => import("../Pages/Workshop/List/index"));
const WorkshopCreate = React.lazy(() => import("../Pages/Workshop/Create"));
const WorkshopHistroy = React.lazy(() => import("../Pages/Workshop/History"));
const NewsletterEmailCreateWorkshop = React.lazy(() => import("../Pages/Workshop/NewsletterEmailCreate/index"));
const NewsletterEmailCreateWorkshopVeiw = React.lazy(() => import("../Pages/Workshop/NewsletterEmailCreate/view"));
const NewsletterEmailCreateWorkshopHistory = React.lazy(() => import("../Pages/Workshop/NewsletterEmailCreate/history"));
const WorkshopfeedBackHistory = React.lazy(() => import("../Pages/Workshop/History/feedBackHistory"));

const PolicyOwnerReportList = React.lazy(() => import("../Pages/PolicyOwnerReport/List/index"));
const Documentation = React.lazy(() => import("../Pages/Documentation"));

/**
 * AppRoutes will load the app routes.
 * @returns
 */
const AppRoutes = () => {
  const { userHasRight, isLoading } = useUser();
  const navigate = useNavigate();

  /* The pre-auth wait, while `useUser()` resolves the session's privileges.
     It renders the same branded page loader as the Suspense fallback below —
     `Loader` is the app's one-line wrapper around `AppLoader variant="page"`,
     so this reuses the shared loader rather than introducing a second one.
     The condition and everything it gates are unchanged; only what the wait
     looks like is different. */
  if (isLoading) {
    return <Loader />;
  }

  return (
    <Routes>
      <Route
        element={<Suspense fallback={<Loader />}></Suspense>}
        path="/fallback"
      />
      <Route
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.DashboardView) ? (
              <Dashboard />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
        path="/dashboard"
      />
      <Route
        path="/dashboard"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.DashboardView) ? (
              <Dashboard />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/setting"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.SettingView) ? <Setting /> : <Dashboard />}
          </Suspense>
        }
      />
      <Route
        path="/department/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.DepartmentView) ? (
              <ListDepartment />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/department/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.DepartmentView) ? (
              <CreateDepartment />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/user/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.UserView) ? <CreateUser /> : <Dashboard />}
          </Suspense>
        }
      />
      <Route
        path="/user/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.UserView) ? <ListUser /> : <Dashboard />}
          </Suspense>
        }
      />
      <Route
        path="/user/role/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.UserView) ? (
              <CreateUserRole />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/user/role/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.UserView) ? (
              <ListUserRole />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/user/privilege/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.UserView) ? (
              <CreateUserPrivilege />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/user/privilege/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.UserView) ? (
              <ListUserPrivilege />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/branch/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.BranchView) ? (
              <ListRestaurant />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/branch/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.BranchView) ? (
              <CreateRestaurant />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/branch/edit/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.BranchView) ? (
              <EditRestaurant />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/theme-color"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ThemeView) ? (
              <ThemeColor />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/printer/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PrinterView) ? (
              <ListPrinter />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/printer/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PrinterView) ? (
              <CreatePrinter />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/team/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.StaffView) ? (
              <CreateDriver />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/team/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.StaffView) ? (
              <ListDriver />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/team-history/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.StaffView) ? (
              <DriverHistory />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/punch/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PunchDetailsView) ? (
              <ListPuchDetails />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/schedule/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ScheduleView) ? (
              <ListSchedule />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/position/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PositionView) ? (
              <ListPosition />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/position/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PositionView) ? (
              <CreatePosition />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/payroll/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PayrollView) ? (
              <ListPayroll />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/payroll/generate"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.GeneratePayroll) ? (
              <ConfirmPayroll />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/team/schedule/pdf/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.StaffView) ? (
              <EmployeePdf />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/all/team/schedule/pdf/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.StaffView) ? (
              <AllEmployeepdf />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />

      <Route
        path="/re-Schedule/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ScheduleView) ? (
              <ReScheduleList />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/re-Schedule/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ScheduleEdit) ? (
              <CreateScheduleList />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/Schedule/list/one-day"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ScheduleView) ? (
              <EmployeeTimeScheduleList />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/payroll/list/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PayrollView) ? (
              <ViewPayroll />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/attendance/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ScheduleEdit) ? (
              <AdminCreateSchedule />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/payroll/list/edit/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PayrollView) ? (
              <EditPayroll />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/payroll/list/emp/report/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PayrollView) ? (
              <EmpReportPayroll />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/emp/report/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PayrollView) ? (
              <EmployeePayrollpdf />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/attendance/weekly/report"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ScheduleView) ? (
              <EmployeeWeeklyReport />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />

      {/*--------------------staf management-------------------------*/}

      <Route
        path="/policy/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <ListProduct />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/policy/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <CreateProduct />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/category/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.CoverageView) ? (
              <ListCategory />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/category/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.CoverageView) ? (
              <CreateCategory />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/looping/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.CoverageView) ? (
              <ListLooping />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/looping/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.CoverageView) ? (
              <CreateLooping />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/brand/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.InsuranceCompanyView) ? (
              <ListProductBrand />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/brand/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.InsuranceCompanyView) ? (
              <CreateProductBrand />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/customer/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <ListCustomer />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/customer/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CreateCustomer />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/customer-history/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CustomerTransaction />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/policy-history/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <ProductDetails />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/looping"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.LoopingView) ? <Looping /> : <Dashboard />}
          </Suspense>
        }
      />

      <Route
        path="/customer-deals/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CustomerDeals />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/profile/customer/:customerId/policy/:policyId"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <CustomerProfile />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/profile/customer/:customerId"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CustomerProfile />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/policy-scenarios/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <ListPolicyScenarios />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/summary-report/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <SummaryReport />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/customer/:customerId/summary/pdf/:policyNumber"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <GeneratePdf />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/customer/:customerId/summary/pdf/secound/:policyNumber"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <SecondpdfGenerate />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/customer/:customerId/policy/:policyNumber"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <CoverageProfile />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/coverage/deal/:dealId"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.CoverageView) ? (
              <EditCoverageDeal />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/customer/:customerId/coverage/:coverageId"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.CoverageView) ? (
              <CreateCoverageDeal />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/coverage-report/customer/:customerId/policy/:policyNumber"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <CoveragePdf />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/coverage-report/customer/:customerId/policy/secound/:policyNumber"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <CoverageSecoundPdf />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/policy-list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <PolicyListList />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/basic/deal/:dealId"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.PoliciesView) ? (
              <BasicDealEdit />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/meeting"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CustomerMeeting />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/meeting/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CreateMeeting />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/conversations/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ToDoView) ? <CreateTodo /> : <Dashboard />}
          </Suspense>
        }
      />
      <Route
        path="/conversations/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ToDoView) ? <ListToDo /> : <Dashboard />}
          </Suspense>
        }
      />
      <Route
        path="/conversations/messages/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ToDoView) ? (
              <ConversationsMessage />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/meeting/setting"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <SettingMeeting />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/meeting/profile/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <ProfileMetting />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/meeting/client/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CreateMeetingClient />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/meeting/staff/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CreateMeetingStaff />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />

      <Route
        path="/email/setting/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.EmailView) ? (
              <EmailSetting />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/email/setting/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.EmailView) ? (
              <EmailSettingCreate />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/email/template/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.EmailView) ? (
              <EmailTemplateCreate />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/newsletters/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.NewslettersView) ? (
              <ListNewsletters />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/newsletters/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.NewslettersView) ? (
              <CreateNewsletters />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client_type/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientTypeView) ? (
              <ListClientType />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client_type/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientTypeView) ? (
              <CreateClientType />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/newsletters"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.NewslettersView) ? (
              <TypeNewsletters />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/newsletters/view/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.NewslettersView) ? (
              <ViewNewsletters />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/newsletters/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.NewslettersView) ? (
              <ListClientEmail />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/newsletters/sms/view/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.NewslettersView) ? (
              <ViewNewslettersSms />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/newsletters/sms/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.NewslettersView) ? (
              <ListClientEmailSms />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/task"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CustomerTask />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/task/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CreateTask />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/task/setting"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <SettingTask />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/task/profile/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <ProfileTask />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/task/client/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CreateTaskClient />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/task/staff/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <CreateTaskStaff />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/report/setting"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.SettingView) ? (
              <ClientReportSetting />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/workshop/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.WorkshopView) ? (
              <WorkshopList />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/workshop/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.WorkshopView) ? (
              <WorkshopCreate />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/workshop/history/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.WorkshopView) ? (
              <WorkshopHistroy />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/workshop/newsletter/e-mail"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.WorkshopView) ? (
              <NewsletterEmailCreateWorkshop />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/workshop/newsletter/e-mail/view/:id"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.WorkshopView) ? (
              <NewsletterEmailCreateWorkshopVeiw />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/workshop/newsletter/e-mail/history"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.WorkshopView) ? (
              <NewsletterEmailCreateWorkshopHistory />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/newsletter/e-mail"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.NewslettersView) ? (
              <ClientEmailCreate />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/client/newsletter/sms"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.NewslettersView) ? (
              <ClientSmsCreate />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
       <Route
        path="/client/report/generate"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.ClientView) ? (
              <PolicyOwnerReportList />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      <Route
        path="/workshop/feedback/list"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.WorkshopView) ? (
              <WorkshopfeedBackHistory />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
       <Route
        path="/sms/template/create"
        element={
          <Suspense fallback={<Loader />}>
            {userHasRight(Privileges.EmailView) ? (
              <SmsTemplateCreate />
            ) : (
              <Dashboard />
            )}
          </Suspense>
        }
      />
      {/*
        Documentation is intentionally not privilege-gated: it explains the app
        to whoever is already signed in, and it holds no client data.
      */}
      <Route
        path="/documentation"
        element={
          <Suspense fallback={<Loader />}>
            <Documentation />
          </Suspense>
        }
      />
      {/*
        One splat route covers every Documentation deep link — the page parses
        the path itself:
          /documentation/guide/:moduleId/:guideId  → Help Center
          /documentation/technical/:moduleId       → engineering handbook
          /documentation/:moduleId                 → legacy links, still work
      */}
      <Route
        path="/documentation/*"
        element={
          <Suspense fallback={<Loader />}>
            <Documentation />
          </Suspense>
        }
      />
      <Route element={<NotFound />} path="*" />
      <Route element={<AccessDenied />} path="/access-denied" />
      <Route element={<Navigate replace to="/dashboard" />} path="" />
    </Routes>
  );
};

export default AppRoutes;
